/**
 * RiskTab - Pestaña de riesgo y entorno urbano
 *
 * @version 1.0
 * @updated 2026-02-12
 * @module core/map-tools/TreeDetailsPanel
 */

import type { Tree } from '../../tree-types';
import { FormLabel } from '../FormLabel';

export interface RiskTabProps {
  tree: Tree;
  onChange: (field: keyof Tree, value: any) => void;
}

export function RiskTab({ tree, onChange }: RiskTabProps): JSX.Element {
  return (
    <div className="tree-tab-panel">
      {/* Sección: Evaluación de Riesgo */}
      <div className="tree-form-section">
        <h3 className="tree-form-section-title">Evaluación de Riesgo</h3>

        <div className="tree-form-group">
          <FormLabel
            required
            tooltip="Evaluación integral del riesgo que representa el árbol considerando su estado, ubicación, tamaño y entorno. Riesgo crítico requiere intervención inmediata."
          >
            Nivel de riesgo global
          </FormLabel>
          <select
            className="tree-form-select"
            value={tree.overallRiskLevel}
            onChange={(e) => onChange('overallRiskLevel', e.target.value)}
          >
            <option value="">Seleccionar...</option>
            <option value="low">Bajo</option>
            <option value="medium">Medio</option>
            <option value="high">Alto</option>
            <option value="critical">Crítico</option>
          </select>
        </div>

        <div className="tree-form-checkbox-group">
          <input
            type="checkbox"
            id="wiringInterference"
            className="tree-form-checkbox"
            checked={tree.wiringInterference}
            onChange={(e) => onChange('wiringInterference', e.target.checked)}
          />
          <label htmlFor="wiringInterference" className="tree-form-checkbox-label">
            Interferencia con cableado eléctrico
          </label>
        </div>

        <div className="tree-form-checkbox-group">
          <input
            type="checkbox"
            id="signageInterference"
            className="tree-form-checkbox"
            checked={tree.signageInterference}
            onChange={(e) => onChange('signageInterference', e.target.checked)}
          />
          <label htmlFor="signageInterference" className="tree-form-checkbox-label">
            Interferencia con señalización
          </label>
        </div>

        <div className="tree-form-checkbox-group">
          <input
            type="checkbox"
            id="buildingInterference"
            className="tree-form-checkbox"
            checked={tree.buildingInterference}
            onChange={(e) => onChange('buildingInterference', e.target.checked)}
          />
          <label htmlFor="buildingInterference" className="tree-form-checkbox-label">
            Interferencia con edificaciones
          </label>
        </div>

        <div className="tree-form-group">
          <FormLabel tooltip="Daño causado por las raíces al levantar o quebrar veredas, baldosas o pavimento. Evaluar severidad para priorizar reparaciones y evitar accidentes.">
            Daño a veredas/pavimento
          </FormLabel>
          <select
            className="tree-form-select"
            value={tree.pavementDamage}
            onChange={(e) => onChange('pavementDamage', e.target.value)}
          >
            <option value="">Seleccionar...</option>
            <option value="none">Ninguno</option>
            <option value="minor">Leve</option>
            <option value="moderate">Moderado</option>
            <option value="severe">Severo</option>
          </select>
        </div>

        <div className="tree-form-checkbox-group">
          <input
            type="checkbox"
            id="trafficObstruction"
            className="tree-form-checkbox"
            checked={tree.trafficObstruction}
            onChange={(e) => onChange('trafficObstruction', e.target.checked)}
          />
          <label htmlFor="trafficObstruction" className="tree-form-checkbox-label">
            Obstrucción visual de tránsito
          </label>
        </div>

        <div className="tree-form-checkbox-group">
          <input
            type="checkbox"
            id="nearSensitiveAreas"
            className="tree-form-checkbox"
            checked={tree.nearSensitiveAreas}
            onChange={(e) => onChange('nearSensitiveAreas', e.target.checked)}
          />
          <label htmlFor="nearSensitiveAreas" className="tree-form-checkbox-label">
            Próximo a áreas sensibles
          </label>
        </div>

        {tree.nearSensitiveAreas && (
          <div className="tree-form-group">
            <label className="tree-form-label">Descripción de áreas sensibles</label>
            <input
              type="text"
              className="tree-form-input"
              value={tree.sensitiveAreasDescription}
              onChange={(e) => onChange('sensitiveAreasDescription', e.target.value)}
              placeholder="Ej: Escuela a 20m, Hospital a 50m"
            />
          </div>
        )}
      </div>

      {/* Sección: Entorno Urbano */}
      <div className="tree-form-section">
        <h3 className="tree-form-section-title">Entorno Urbano</h3>

        <div className="tree-form-group">
          <FormLabel tooltip="Tipo de superficie que rodea la base del árbol. Suelo o césped permiten mejor infiltración de agua y aire; pavimento limita el crecimiento radicular.">
            Tipo de suelo circundante
          </FormLabel>
          <select
            className="tree-form-select"
            value={tree.surroundingSoilType}
            onChange={(e) => onChange('surroundingSoilType', e.target.value)}
          >
            <option value="">Seleccionar...</option>
            <option value="soil">Tierra</option>
            <option value="grass">Césped</option>
            <option value="pavement">Pavimento</option>
            <option value="mixed">Mixto</option>
          </select>
        </div>

        <div className="tree-form-group">
          <FormLabel tooltip="Alcorque o cazuela: es el espacio rectangular o cuadrado excavado en el pavimento alrededor del tronco, que permite la infiltración de agua y el crecimiento de raíces. También se le llama 'arenero' o 'espejo'.">
            Alcorque/Cazuela
          </FormLabel>
          <div className="tree-form-checkbox-group">
            <input
              type="checkbox"
              id="hasTreeGrate"
              className="tree-form-checkbox"
              checked={tree.hasTreeGrate}
              onChange={(e) => onChange('hasTreeGrate', e.target.checked)}
            />
            <label htmlFor="hasTreeGrate" className="tree-form-checkbox-label">
              Tiene alcorque/cazuela
            </label>
          </div>
        </div>

        {tree.hasTreeGrate && (
          <div className="tree-form-row">
            <div className="tree-form-group">
              <label className="tree-form-label">Largo (cm)</label>
              <input
                type="number"
                className="tree-form-input"
                value={tree.grateLength || ''}
                onChange={(e) => onChange('grateLength', e.target.value ? parseFloat(e.target.value) : undefined)}
                placeholder="0"
                min="0"
              />
            </div>

            <div className="tree-form-group">
              <label className="tree-form-label">Ancho (cm)</label>
              <input
                type="number"
                className="tree-form-input"
                value={tree.grateWidth || ''}
                onChange={(e) => onChange('grateWidth', e.target.value ? parseFloat(e.target.value) : undefined)}
                placeholder="0"
                min="0"
              />
            </div>
          </div>
        )}

        <div className="tree-form-row">
          <div className="tree-form-group">
            <FormLabel tooltip="Espacio disponible en el suelo para el desarrollo del sistema radicular. Espacio limitado restringe el crecimiento y estabilidad del árbol.">
              Espacio para raíces
            </FormLabel>
            <select
              className="tree-form-select"
              value={tree.rootSpace}
              onChange={(e) => onChange('rootSpace', e.target.value)}
            >
              <option value="">Seleccionar...</option>
              <option value="sufficient">Suficiente</option>
              <option value="limited">Limitado</option>
              <option value="very-limited">Muy limitado</option>
            </select>
          </div>

          <div className="tree-form-group">
            <FormLabel tooltip="Espacio aéreo disponible para el desarrollo de la copa. Evaluar proximidad a edificios, cables y otros árboles que puedan limitar su crecimiento.">
              Espacio para copa
            </FormLabel>
            <select
              className="tree-form-select"
              value={tree.crownSpace}
              onChange={(e) => onChange('crownSpace', e.target.value)}
            >
              <option value="">Seleccionar...</option>
              <option value="sufficient">Suficiente</option>
              <option value="limited">Limitado</option>
              <option value="very-limited">Muy limitado</option>
            </select>
          </div>
        </div>

        <div className="tree-form-checkbox-group">
          <input
            type="checkbox"
            id="hasStakes"
            className="tree-form-checkbox"
            checked={tree.hasStakes}
            onChange={(e) => onChange('hasStakes', e.target.checked)}
          />
          <label htmlFor="hasStakes" className="tree-form-checkbox-label">
            Tiene tutores/protectores
          </label>
        </div>

        {tree.hasStakes && (
          <div className="tree-form-group">
            <FormLabel tooltip="Tipo de soporte utilizado para mantener el árbol erguido (tutor de madera, varillas metálicas, protector plástico, etc.). Común en árboles jóvenes.">
              Tipo de tutores
            </FormLabel>
            <input
              type="text"
              className="tree-form-input"
              value={tree.stakesType}
              onChange={(e) => onChange('stakesType', e.target.value)}
              placeholder="Ej: Tutor de madera, protector metálico"
            />
          </div>
        )}

        <div className="tree-form-group">
          <FormLabel tooltip="Distancia a la luminaria más cercana. Importante para evaluar interferencias con el cableado y la poda requerida para mantener la iluminación efectiva.">
            Iluminación pública cercana
          </FormLabel>
          <select
            className="tree-form-select"
            value={tree.streetLightDistance}
            onChange={(e) => onChange('streetLightDistance', e.target.value)}
          >
            <option value="">Seleccionar...</option>
            <option value="<3m">Menos de 3 metros</option>
            <option value="3-5m">Entre 3 y 5 metros</option>
            <option value=">5m">Más de 5 metros</option>
            <option value="none">Sin iluminación</option>
          </select>
        </div>

        <div className="tree-form-row">
          <div className="tree-form-group">
            <FormLabel tooltip="Intensidad del flujo de peatones en la zona. Alto tráfico aumenta el riesgo en caso de caída de ramas y requiere mayor atención al mantenimiento.">
              Tráfico peatonal
            </FormLabel>
            <select
              className="tree-form-select"
              value={tree.pedestrianTraffic}
              onChange={(e) => onChange('pedestrianTraffic', e.target.value)}
            >
              <option value="">Seleccionar...</option>
              <option value="low">Bajo</option>
              <option value="medium">Medio</option>
              <option value="high">Alto</option>
            </select>
          </div>

          <div className="tree-form-group">
            <FormLabel tooltip="Intensidad del tránsito vehicular cercano. Afecta la exposición del árbol a contaminación, vibraciones y posibles impactos mecánicos.">
              Tráfico vehicular
            </FormLabel>
            <select
              className="tree-form-select"
              value={tree.vehicularTraffic}
              onChange={(e) => onChange('vehicularTraffic', e.target.value)}
            >
              <option value="">Seleccionar...</option>
              <option value="low">Bajo</option>
              <option value="medium">Medio</option>
              <option value="high">Alto</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
}
